import { defineStore, acceptHMRUpdate } from 'pinia';
import {
  CONDITIONALS,
  EMPTY_INPUT,
  WORKLIST_ATTRIBUTES,
} from '@gameplan/worklists/query-builder/constants';
import { useGamePlanStore } from './useGamePlans';
import {
  normalizeWorkListTypeId,
  worklistNameUniquenessKey,
} from '@/utils/worklistRanking';
import { toast } from '@/composables/useAlertToast';
import { isBefore } from 'date-fns';

export const DEFAULT_WORKLIST = {
  work_list_name: '',
  owner: '',
  owner_id: null,
  assignees: [],
  is_priority: false,
  description: '',
  start_timest: null,
  end_timest: null,
  work_list_type: 5,
};

export const DEFAULT_QUERY = {
  condition: 'AND',
  rules: [],
  valid: false,
};

export const useQueryBuilderStore = defineStore('queryBuilder', {
  state: () => {
    return {
      worklist: {
        ...DEFAULT_WORKLIST,
      },
      query: {
        ...DEFAULT_QUERY,
      },
      field_options: [],
      attempt: false,
      defaultWorklist: false,
      disabledDuringAttempt: false,
      columnValuesLookup: {
        billing_entity: [],
      },
    };
  },
  getters: {
    getAttempt: state => {
      return state.attempt;
    },
    getQuery: state => {
      return state.query;
    },
    getWorklist: state => {
      return state.worklist;
    },
    getFields: () => {
      return Object.values(WORKLIST_ATTRIBUTES)
        .map(field => ({
          value: field.label,
          ...field,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    },
    getOperators: () => {
      return field => {
        const attribute = WORKLIST_ATTRIBUTES[field];
        if (!attribute) return [];
        return WORKLIST_ATTRIBUTES[field].operators.map(operator => ({
          value: operator.label,
          ...attribute,
          ...operator,
        }));
      };
    },
    getAttributes: state => {
      return (level, indexChain) => {
        if (level === 0) {
          return state.query.rules[indexChain[0]];
        } else if (level === 1) {
          return state.query.rules[indexChain[0]].rules[indexChain[1]];
        } else if (level === 2) {
          return state.query.rules[indexChain[0]].rules[indexChain[1]].rules[
            indexChain[2]
          ];
        } else {
          console.error('Not Supported');
        }
      };
    },
  },
  actions: {
    setAttempt(val) {
      this.attempt = val;
    },
    setDisabledDuringAttempt(val) {
      this.disabledDuringAttempt = val;
    },
    setValidity(isValid) {
      this.query.valid = isValid;
    },
    validateRules() {
      console.debug('validateRules: Starting validation');

      if (this.defaultWorklist) {
        console.debug(
          'validateRules: Using default worklist, setting query as valid'
        );
        this.query = {
          ...DEFAULT_QUERY,
          valid: true,
        };
        return true;
      } else if (this.query.rules?.length == 0) {
        console.debug(
          'validateRules: No rules found, setting validity to false'
        );
        this.setValidity(false);
        return false;
      }

      console.debug('validateRules: Processing rules validation');

      // write logic to go through and check if the schema generated is valid
      const { query } = this;
      // get all the rules
      const initial_rules = query.rules;
      console.debug(
        'validateRules: Initial rules count:',
        initial_rules.length
      );

      const all_the_rules = [];

      // flatten them for comparison
      function flatten_rules(rules) {
        for (const rule of rules) {
          if (rule.id) {
            console.debug(
              'validateRules: Adding rule to flattened list:',
              rule.id
            );
            all_the_rules.push(rule);
          } else {
            console.debug(
              'validateRules: Found nested rules group, flattening'
            );
            flatten_rules(rule.rules);
          }
        }
      }

      flatten_rules(initial_rules);

      console.debug(
        'validateRules: Total flattened rules count:',
        all_the_rules.length
      );

      // go through and check there's a field
      let is_valid_schema = true;

      // allowed nulls
      const allowed_nulls = [
        'is_null',
        'is_not_null',
        'pg_array_null',
        'pg_array_not_null',
      ];
      console.debug('validateRules: Allowed null operators:', allowed_nulls);

      for (const rule of all_the_rules) {
        console.debug('validateRules: Validating rule:', rule.id, {
          field: rule.field,
          operator: rule.operator,
          value: rule.value,
        });

        if (!rule.field || !rule.operator) {
          console.debug(
            'validateRules: Rule invalid - missing field or operator'
          );
          //if there are empties in the field or operator, it's not valid
          is_valid_schema = false;
          break;
        }

        // get the rule depending on type or if it is null
        let value;
        if (rule?.value === null) {
          console.debug(
            'validateRules: Rule value is null, setting value to false'
          );
          value = false;
        } else if (typeof rule?.value === 'string') {
          console.debug('validateRules: Rule value is string, trimming');
          value = rule.value.trim();
        } else if (typeof rule?.value === 'object') {
          console.debug(
            'validateRules: Rule value is object, extracting values'
          );
          value = [...Object.values(rule?.value)];
        }

        console.debug('validateRules: Processed value:', value);

        if (
          // if there's no value and the operator isn't in allowed_nulls
          !value &&
          !allowed_nulls.includes(rule.operator)
        ) {
          console.debug(
            'validateRules: Rule invalid - no value and operator not in allowed nulls'
          );
          is_valid_schema = false;
          break;
        }

        // if the object is an array and has no elements
        if (Array.isArray(value) && !value.length) {
          console.debug('validateRules: Rule invalid - array value is empty');
          is_valid_schema = false;
          break;
        }

        // the object is an array and has unallowed null value(s)
        if (Array.isArray(value) && value.includes(null)) {
          console.debug(
            'validateRules: Rule invalid - array contains null values'
          );
          is_valid_schema = false;
          break;
        }

        if (
          (rule.type === 'date' || rule.operator.endsWith('_date')) &&
          Array.isArray(value) &&
          rule.value.length > 1 &&
          !isBefore(new Date(rule.value[0]), new Date(rule.value[1]))
        ) {
          console.debug(
            'validateRules: Rule invalid - dates not in ascending order'
          );
          toast.error('Dates must be in ascending order!');
          is_valid_schema = false;
          break;
        }

        if (
          rule.operator.includes('between') &&
          rule.value[1] - rule.value[0] <= 0
        ) {
          console.debug(
            'validateRules: Rule invalid - between values not in correct order'
          );
          toast.error(`Second value must be greater than ${rule.value[0]}`, {
            id: 'min-max-error',
          });
          is_valid_schema = false;
          break;
        }

        console.debug('validateRules: Rule passed validation:', rule.id);
      }

      console.debug('validateRules: Final validation result:', is_valid_schema);
      this.setValidity(is_valid_schema);
      return is_valid_schema;
    },
    validateWorklist(editingWorklist) {
      // Check for required inputs
      if (
        !this.defaultWorklist &&
        (!this.worklist.work_list_name?.trim() ||
          !this.worklist.description?.trim())
      ) {
        return false;
      }

      // Check if end date is required for priority worklists
      if (
        this.worklist.is_priority &&
        !this.worklist.end_timest &&
        !this.defaultWorklist
      ) {
        return false;
      }

      const gameplan_store = useGamePlanStore();

      // Check for existing worklist name on new worklists
      if (!editingWorklist) {
        const newNameKey = worklistNameUniquenessKey(
          this.worklist.work_list_name
        );
        const newType = normalizeWorkListTypeId(this.worklist.work_list_type);
        const matchingNames = gameplan_store.previous_worklists.filter(wl => {
          return (
            worklistNameUniquenessKey(wl.work_list_name) === newNameKey &&
            normalizeWorkListTypeId(wl.work_list_type) === newType
          );
        });

        if (!matchingNames.length) return true;

        const unExpiredMatchingName = matchingNames.some(worklist => {
          return (
            new Date(worklist.end_timest).setHours(0, 0, 0, 0) >=
              new Date().setHours(0, 0, 0, 0) || !worklist.end_timest
          );
        });

        if (unExpiredMatchingName) {
          toast.error('Existing worklist name found.');
          return false;
        }
      }

      // Check for existing worklist name on editing worklists
      else {
        const newNameKey = worklistNameUniquenessKey(
          this.worklist.work_list_name
        );
        const newType = normalizeWorkListTypeId(this.worklist.work_list_type);
        const matching = gameplan_store.previous_worklists.filter(wl => {
          if (wl.work_list_id === this.worklist.work_list_id) return false;
          return (
            worklistNameUniquenessKey(wl.work_list_name) === newNameKey &&
            normalizeWorkListTypeId(wl.work_list_type) === newType
          );
        });
        if (!matching.length) return true;
        const unexpired = matching.some(wl => {
          return (
            !wl.end_timest ||
            new Date(wl.end_timest).setHours(0, 0, 0, 0) >=
              new Date().setHours(0, 0, 0, 0)
          );
        });
        if (unexpired) {
          toast.error('Existing worklist name found.');
          return false;
        }
      }
      return true;
    },
    updateWorklist(value) {
      this.worklist = {
        ...this.worklist,
        ...value,
      };
    },
    setConditional(condition, level, indexChain) {
      if (level === 0) {
        this.query = {
          ...this.query,
          condition,
        };
      } else if (level === 1) {
        const updatedRuleGroup = {
          ...this.query.rules[indexChain[0]],
          condition,
        };

        this.query.rules[indexChain[0]] = updatedRuleGroup;
      } else if (level === 2) {
        const updatedRuleGroup = {
          ...this.query.rules[indexChain[0]].rules[indexChain[1]],
          condition,
        };

        this.query.rules[indexChain[0]].rules[indexChain[1]] = updatedRuleGroup;
      } else {
        console.error('Not supported.');
      }
    },
    updateAttribute(event, level, indexChain, attributeName) {
      if (level === 0) {
        const updatedObject = {
          ...this.query.rules[indexChain[0]],
          [attributeName]: event.key,
        };

        this.query.rules[indexChain[0]] = updatedObject;
      } else if (level === 1) {
        const updatedObject = {
          ...this.query.rules[indexChain[0]].rules[indexChain[1]],
          [attributeName]: event.key,
        };

        this.query.rules[indexChain[0]].rules[indexChain[1]] = updatedObject;
      } else if (level === 2) {
        const updatedObject = {
          ...this.query.rules[indexChain[0]].rules[indexChain[1]].rules[
            indexChain[2]
          ],
          [attributeName]: event.key,
        };

        this.query.rules[indexChain[0]].rules[indexChain[1]].rules[
          indexChain[2]
        ] = updatedObject;
      } else {
        console.error('Not Supported');
      }
    },
    updateAttributes(level, indexChain, object) {
      if (level === 0) {
        const updatedObject = {
          ...this.query.rules[indexChain[0]],
          ...object,
        };

        this.query.rules[indexChain[0]] = updatedObject;
      } else if (level === 1) {
        const updatedObject = {
          ...this.query.rules[indexChain[0]].rules[indexChain[1]],
          ...object,
        };

        this.query.rules[indexChain[0]].rules[indexChain[1]] = updatedObject;
      } else if (level === 2) {
        const updatedObject = {
          ...this.query.rules[indexChain[0]].rules[indexChain[1]].rules[
            indexChain[2]
          ],
          ...object,
        };

        this.query.rules[indexChain[0]].rules[indexChain[1]].rules[
          indexChain[2]
        ] = updatedObject;
      } else {
        console.error('Not Supported');
      }
    },
    resetWorklist() {
      console.debug(`Resetting worklist`);
      this.worklist = { ...DEFAULT_WORKLIST };
    },
    resetQuery() {
      console.debug(`Resetting query.`);
      this.query = { ...DEFAULT_QUERY };
    },
    setRulesSchema(rules) {
      this.query = rules;
    },
    addRule(level, indexChain, keyChain, newKey) {
      console.debug(
        `Creating rule at level ${level} at path ${
          ' > ' + keyChain?.filter(e => e)?.join(' > ')
        } with key ${newKey}.`
      );

      if (level === 0) {
        this.query.rules = [
          ...this.query.rules,
          {
            id: newKey,
            ...EMPTY_INPUT,
          },
        ];
      } else if (level === 1) {
        this.query.rules[indexChain[0]].rules = [
          ...this.query.rules[indexChain[0]].rules,
          {
            id: newKey,
            ...EMPTY_INPUT,
          },
        ];
      } else if (level === 2) {
        this.query.rules[indexChain[0]].rules[indexChain[1]].rules = [
          ...this.query.rules[indexChain[0]].rules[indexChain[1]].rules,
          {
            id: newKey,
            ...EMPTY_INPUT,
          },
        ];
      } else {
        console.error('Not supported.');
      }
    },
    addGroup(level, indexChain, keyChain, newKey) {
      console.debug(
        `Creating group at level ${level} at path ${
          ' > ' + keyChain?.filter(e => e)?.join(' > ')
        } with key ${newKey}.`
      );

      if (level === 0) {
        this.query.rules = [
          ...this.query.rules,
          {
            condition: CONDITIONALS.AND,
            rules: [
              {
                id: newKey,
                ...EMPTY_INPUT,
              },
            ],
          },
        ];
      } else if (level === 1) {
        this.query.rules[indexChain[0]].rules = [
          ...this.query.rules[indexChain[0]].rules,
          {
            condition: CONDITIONALS.AND,
            rules: [
              {
                id: newKey,
                ...EMPTY_INPUT,
              },
            ],
          },
        ];
      } else {
        console.error('Not Supported.');
      }
    },
    remove(level, indexChain, keyChain, isGroup) {
      console.debug(
        `Removing at level ${level} at path ${` > ${keyChain
          ?.filter(e => e)
          ?.join(' > ')}`}.`
      );

      if (level === 0) {
        this.query.rules.splice(indexChain[0], 1);
      } else if (level === 1) {
        if (isGroup || this.query.rules[indexChain[0]].rules.length === 1)
          this.query.rules.splice(indexChain[0], 1);
        else {
          this.query.rules[indexChain[0]].rules.splice(indexChain[1], 1);
        }
      } else if (level === 2) {
        if (
          isGroup ||
          this.query.rules[indexChain[0]].rules[indexChain[1]].rules.length ===
            1
        ) {
          if (this.query.rules[indexChain[0]].rules.length === 1) {
            this.query.rules.splice(indexChain[0], 1);
          } else {
            this.query.rules[indexChain[0]].rules.splice(indexChain[1], 1);
          }
        } else {
          this.query.rules[indexChain[0]].rules[indexChain[1]].rules.splice(
            indexChain[2],
            1
          );
        }
      } else {
        console.error('Not supported.');
      }
    },
  },
});

// Enable Hot Module Replacement (HMR)
if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useQueryBuilderStore, import.meta.hot)
  );
}
